/**
 * Assessment Sharing Service
 * Enables couples to take assessments together and compare results
 * Based on Dive Deep Dating session architecture
 */

import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  onSnapshot,
  serverTimestamp,
  deleteDoc,
  Timestamp
} from 'firebase/firestore';
import { db } from './firebase';

/**
 * Sessions expire this many days after their last activity. The Firestore
 * rules deny `get` once expiresAt has passed. Deleting the document itself
 * needs a Firestore TTL policy on expiresAt, which requires billing the
 * project doesn't have yet; until then scripts/purge-expired-shared-assessments.mjs
 * removes expired documents when it's run (see firestore.rules).
 */
const SESSION_TTL_DAYS = 7;

/**
 * The expiry stamp written at creation and refreshed on every activity.
 * Computed from the device clock; the rules cap it at 8 days out, which
 * absorbs ordinary clock skew while making immortal sessions unmintable.
 */
function sessionExpiryTimestamp() {
  return Timestamp.fromMillis(Date.now() + SESSION_TTL_DAYS * 24 * 60 * 60 * 1000);
}

/**
 * The rules reject an expired (or missing) session with permission-denied
 * rather than not-found, so translate that into words a couple can act on.
 */
function friendlyError(error, fallback) {
  if (error && error.code === 'permission-denied') {
    return 'This shared session has expired. Sessions close 7 days after their last activity. Start a new one to continue.';
  }
  return (error && error.message) || fallback;
}

/**
 * Generate an 8-character session code.
 *
 * The code is the only thing protecting a couple's responses and scores —
 * these sessions are deliberately anonymous, so the Firestore rules can't
 * check request.auth and instead allow a `get` by whoever knows the code.
 * That makes the code a security token, so it's drawn from the crypto RNG
 * rather than Math.random(), whose output is predictable from observed
 * values in some engines. Eight characters over a 32-character alphabet
 * gives ~1.1 trillion codes, putting blind guessing out of reach even
 * before the 7-day expiry shrinks the pool of live sessions.
 *
 * Rejection sampling keeps the 32-character alphabet uniform: 256 is a
 * multiple of 32, so no bias correction is needed for the byte range used.
 */
function generateSessionCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Exclude confusing characters
  const bytes = new Uint8Array(8);

  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    // Very old browsers only — keeps session sharing working rather than
    // throwing, at the cost of a weaker code.
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }

  let code = '';
  for (let i = 0; i < bytes.length; i++) {
    code += chars.charAt(bytes[i] % chars.length);
  }
  return code;
}

/**
 * Create a new shared assessment session
 */
export async function createSharedAssessment(assessmentId, partnerOneName) {
  const sessionCode = generateSessionCode();

  const sessionData = {
    sessionCode,
    assessmentId,
    status: 'waiting_for_partner', // waiting_for_partner, both_taking, completed
    createdAt: serverTimestamp(),
    lastActivity: serverTimestamp(),
    expiresAt: sessionExpiryTimestamp(),
    partnerOne: {
      name: partnerOneName,
      joinedAt: serverTimestamp(),
      status: 'not_started', // not_started, in_progress, completed
      responses: [],
      scores: null
    },
    partnerTwo: {
      name: null,
      joinedAt: null,
      status: 'not_joined',
      responses: [],
      scores: null
    }
  };

  try {
    await setDoc(doc(db, 'sharedAssessments', sessionCode), sessionData);
    console.log('✅ Created shared assessment session:', sessionCode);
    return { success: true, sessionCode };
  } catch (error) {
    console.error('❌ Failed to create shared assessment:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Partner joins existing assessment session
 */
export async function joinSharedAssessment(sessionCode, partnerTwoName) {
  try {
    const sessionRef = doc(db, 'sharedAssessments', sessionCode.toUpperCase());
    const sessionSnap = await getDoc(sessionRef);

    if (!sessionSnap.exists()) {
      return { success: false, error: 'Session not found' };
    }

    const sessionData = sessionSnap.data();

    // Check if partner two already joined
    if (sessionData.partnerTwo.name) {
      return { success: false, error: 'Partner has already joined this session' };
    }

    // Update with partner two details
    await updateDoc(sessionRef, {
      'partnerTwo.name': partnerTwoName,
      'partnerTwo.joinedAt': serverTimestamp(),
      'partnerTwo.status': 'not_started',
      status: 'both_taking',
      lastActivity: serverTimestamp(),
      expiresAt: sessionExpiryTimestamp()
    });

    console.log('✅ Partner joined session:', sessionCode);
    return { success: true, sessionCode, assessmentId: sessionData.assessmentId };
  } catch (error) {
    console.error('❌ Failed to join shared assessment:', error);
    return { success: false, error: friendlyError(error, 'Failed to join shared assessment') };
  }
}

/**
 * Get shared assessment session data
 */
export async function getSharedAssessment(sessionCode) {
  try {
    const sessionRef = doc(db, 'sharedAssessments', sessionCode.toUpperCase());
    const sessionSnap = await getDoc(sessionRef);

    if (!sessionSnap.exists()) {
      return { success: false, error: 'Session not found' };
    }

    return { success: true, data: sessionSnap.data() };
  } catch (error) {
    console.error('❌ Failed to get shared assessment:', error);
    return { success: false, error: friendlyError(error, 'Failed to get shared assessment') };
  }
}

/**
 * Update partner progress (responses and scores)
 */
export async function updatePartnerProgress(sessionCode, partnerNumber, responses, scores, status) {
  try {
    const sessionRef = doc(db, 'sharedAssessments', sessionCode.toUpperCase());
    const partnerKey = partnerNumber === 1 ? 'partnerOne' : 'partnerTwo';

    const updates = {
      [`${partnerKey}.responses`]: responses,
      [`${partnerKey}.scores`]: scores,
      [`${partnerKey}.status`]: status,
      lastActivity: serverTimestamp(),
      expiresAt: sessionExpiryTimestamp()
    };

    await updateDoc(sessionRef, updates);
    console.log(`✅ Updated partner ${partnerNumber} progress`);
    return { success: true };
  } catch (error) {
    console.error(`❌ Failed to update partner ${partnerNumber} progress:`, error);
    return { success: false, error: error.message };
  }
}

/**
 * Subscribe to real-time updates for shared assessment
 */
export function subscribeToSharedAssessment(sessionCode, callback) {
  const sessionRef = doc(db, 'sharedAssessments', sessionCode.toUpperCase());

  return onSnapshot(sessionRef, (snapshot) => {
    if (snapshot.exists()) {
      callback({ success: true, data: snapshot.data() });
    } else {
      callback({ success: false, error: 'Session not found' });
    }
  }, (error) => {
    console.error('❌ Shared assessment subscription error:', error);
    callback({ success: false, error: friendlyError(error, 'Lost connection to the shared session') });
  });
}

/**
 * Mark session as completed when both partners finish
 */
export async function completeSharedAssessment(sessionCode) {
  try {
    const sessionRef = doc(db, 'sharedAssessments', sessionCode.toUpperCase());

    await updateDoc(sessionRef, {
      status: 'completed',
      completedAt: serverTimestamp(),
      lastActivity: serverTimestamp(),
      expiresAt: sessionExpiryTimestamp()
    });

    console.log('✅ Shared assessment completed:', sessionCode);
    return { success: true };
  } catch (error) {
    console.error('❌ Failed to complete shared assessment:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Delete a shared assessment session
 */
export async function deleteSharedAssessment(sessionCode) {
  try {
    await deleteDoc(doc(db, 'sharedAssessments', sessionCode.toUpperCase()));
    console.log('✅ Deleted shared assessment:', sessionCode);
    return { success: true };
  } catch (error) {
    console.error('❌ Failed to delete shared assessment:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Compare partner scores and calculate alignment
 * Uses the assessment's comparePartnerScores function
 */
export function compareScores(assessment, partnerOneScores, partnerTwoScores) {
  if (!assessment.comparePartnerScores) {
    console.warn('Assessment does not support partner comparison');
    return null;
  }

  const alignmentScores = assessment.comparePartnerScores(partnerOneScores, partnerTwoScores);
  const alignmentType = assessment.getAlignmentType
    ? assessment.getAlignmentType(alignmentScores, partnerOneScores, partnerTwoScores)
    : null;

  return {
    alignmentScores,
    alignmentType,
    partnerOneScores,
    partnerTwoScores
  };
}

export default {
  createSharedAssessment,
  joinSharedAssessment,
  getSharedAssessment,
  updatePartnerProgress,
  subscribeToSharedAssessment,
  completeSharedAssessment,
  deleteSharedAssessment,
  compareScores
};
